import { cn } from "@/lib/utils";
import React from "react";
import { BookOpen, Wrench, Code, PenTool } from "lucide-react";

import {
  Section,
  SectionContainer,
  SectionHeader,
  SectionTitle,
  SectionDescription,
  SectionContent,
} from "@/components/ui/section";

// Group technologies by category
const techCategories = [
  {
    title: "Frontend Development",
    icon: <Code className="h-5 w-5 text-primary" />,
    description:
      "Modern technologies for building responsive and interactive user interfaces",
    technologies: [
      {
        img: "/images/tech/react.png",
        title: "React",
        description: "JavaScript Library",
      },
      {
        img: "/images/tech/framer.png",
        title: "Framer Motion",
        description: "Animation Library",
      },
      {
        img: "/images/tech/tailwind.png",
        title: "Tailwind CSS",
        description: "CSS Framework",
      },
      {
        img: "/images/tech/vite.png",
        title: "Vite",
        description: "Frontend Build Tool",
      },
    ],
  },
  {
    title: "Design & UX Tools",
    icon: <PenTool className="h-5 w-5 text-primary" />,
    description:
      "Professional tools for creating beautiful and usable interfaces",
    technologies: [
      {
        img: "/images/tech/figma.png",
        title: "Figma",
        description: "UI/UX Design Tool",
      },
      {
        img: "/images/tech/ai.png",
        title: "Adobe Illustrator",
        description: "Vector Graphics Editor",
      },
    ],
  },
  {
    title: "Development Environment",
    icon: <Wrench className="h-5 w-5 text-primary" />,
    description: "Tools that help maintain high productivity and code quality",
    technologies: [
      {
        img: "/images/tech/vsc.png",
        title: "Visual Studio Code",
        description: "Code Editor",
      },
      {
        img: "/images/tech/nodejs-icon.svg",
        title: "Node.js",
        description: "JavaScript Runtime",
      },
    ],
  },
];

// Additional tech skills that aren't represented by the main categories
const additionalSkills = [
  "JavaScript ES6+",
  "TypeScript",
  "HTML5",
  "CSS3/SASS",
  "Git/GitHub",
  "REST APIs",
  "Redux",
  "React Router",
  "Responsive Design",
  "Next.js",
];

const Tech = () => {
  return (
    <Section id="tech">
      <SectionContainer>
        <SectionHeader>
          <SectionTitle>Technologies & Skills</SectionTitle>
          <SectionDescription>
            I use modern technologies and best practices to create exceptional
            digital experiences. My toolkit is constantly evolving as I stay
            updated with the latest industry standards.
          </SectionDescription>
        </SectionHeader>
        <SectionContent>
          {/* Tech categories */}
          <div className="space-y-16">
            {techCategories.map((category, categoryIndex) => (
              <div key={categoryIndex} className="space-y-6">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                    {category.icon}
                  </div>
                  <div>
                    <h3 className="text-xl font-medium">{category.title}</h3>
                    <p className="text-sm text-muted-foreground">
                      {category.description}
                    </p>
                  </div>
                </div>

                <div className="flex gap-4 flex-wrap items-center">
                  {category.technologies.map(
                    ({ img, title, description }, index) => (
                      <div
                        key={index}
                        className={cn(
                          "group relative sm:p-3 rounded-md overflow-hidden transition-all duration-300",
                          "border border-accent/20 bg-background",
                          "hover:shadow-[0_2px_12px_rgba(0,0,0,0.03)] dark:hover:shadow-[0_2px_12px_rgba(255,255,255,0.03)]",
                          "hover:-translate-y-0.5 will-change-transform"
                        )}
                      >
                        <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                          <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(0,0,0,0.02)_1px,transparent_1px)] dark:bg-[radial-gradient(circle_at_center,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[length:4px_4px]" />
                        </div>

                        <div className="flex items-center gap-3">
                          <div className="flex">
                            <div className="w-[52px] h-[52px] p-3 rounded-xl bg-zinc-100 dark:bg-zinc-800 text-zinc-900 dark:text-zinc-100 flex items-center justify-center">
                              <div className="relative">
                                <div className="absolute inset-0 bg-gradient-to-r from-blue-500/30 to-blue-500/30 blur-2xl rounded-full" />
                                <img
                                  src={img}
                                  alt={title}
                                  className="object-cover w-7 relative z-10"
                                />
                              </div>
                            </div>
                          </div>

                          <div className="hidden sm:block space-y-1">
                            <h3 className="font-medium text-gray-900 dark:text-gray-100 tracking-tight text-md">
                              {title}
                            </h3>

                            <p className="text-muted-foreground text-sm leading-snug font-[425]">
                              {description}
                            </p>
                          </div>
                        </div>

                        <div className="absolute inset-0 -z-10 rounded-xl p-px bg-gradient-to-br from-transparent via-gray-100/50 to-transparent dark:via-white/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                      </div>
                    )
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Additional skills */}
          <div className="mt-16 p-6 border border-accent/20 rounded-xl bg-accent/5">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <BookOpen className="h-5 w-5 text-primary" />
              </div>
              <div>
                <h3 className="text-xl font-medium">
                  Additional Skills & Expertise
                </h3>
                <p className="text-sm text-muted-foreground">
                  Other technologies and skills in my development toolbox
                </p>
              </div>
            </div>

            <div className="flex flex-wrap gap-2">
              {additionalSkills.map((skill, index) => (
                <span
                  key={index}
                  className="px-3 py-1.5 bg-background rounded-md border border-accent/20 text-sm hover:border-accent/40 transition-colors"
                >
                  {skill}
                </span>
              ))}
            </div>
          </div>
        </SectionContent>
      </SectionContainer>
    </Section>
  );
};

export default Tech;
